import { useMutation } from '@tanstack/react-query';
import {
  generateInvitationApiKey,
  isInvitationTokenErrorCode,
  SESSION_EXPIRED_CODE,
  type InvitationTokenErrorCode,
} from '../../infrastructure/api/invitation.api.repository';

export interface GenerateApiKeyTokenError {
  kind: 'token';
  code: InvitationTokenErrorCode;
  message?: string;
}

export interface GenerateApiKeySessionExpiredError {
  kind: 'sessionExpired';
}

export interface GenerateApiKeyNetworkError {
  kind: 'network';
  message: string;
}

export type GenerateApiKeyMutationError =
  | GenerateApiKeyTokenError
  | GenerateApiKeySessionExpiredError
  | GenerateApiKeyNetworkError;

export function useGenerateApiKey() {
  const mutation = useMutation({
    mutationFn: ({ token }: { token: string }) =>
      generateInvitationApiKey(token),
  });

  const error: GenerateApiKeyMutationError | null =
    mutation.error != null
      ? (() => {
          const err = mutation.error as Error & { code?: unknown };
          if (err.code === SESSION_EXPIRED_CODE) {
            return { kind: 'sessionExpired' };
          }
          if (isInvitationTokenErrorCode(err.code)) {
            return {
              kind: 'token',
              code: err.code,
              message: err.message,
            };
          }
          return {
            kind: 'network',
            message:
              err.message ||
              'Impossible de contacter le serveur. Vérifiez votre connexion.',
          };
        })()
      : null;

  return {
    generateApiKey: mutation.mutate,
    public_api_integration_id: mutation.data?.public_api_integration_id,
    api_key_secret: mutation.data?.api_key_secret,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error,
    reset: mutation.reset,
  };
}
